import { commitMutation, graphql } from "react-relay";
import { Environment } from "react-relay";
import { PayloadError } from "relay-runtime";

import {
  CreateLabeledPalletMutationInput,
  CreateLabeledPalletMutationResponse
} from "../__generated__/CreateLabeledPalletMutation.graphql";

import { FrameViewer_viewer } from "../__generated__/FrameViewer_viewer.graphql";

const mutation = graphql`
  mutation CreateLabeledPalletMutation(
    $input: CreateLabeledPalletMutationInput!
  ) {
    createLabeledPallet(input: $input) {
      clientMutationId
      labeledPallet {
        id
        frontPockets {
          left {
            corners {
              id
              x
              y
              visibility
            }
          }
          right {
            corners {
              id
              x
              y
              visibility
            }
          }
        }
        sidePockets {
          left {
            corners {
              id
              x
              y
              visibility
            }
          }
          right {
            corners {
              id
              x
              y
              visibility
            }
          }
        }
        objectType
        surfaceType
        author {
          id
          username
        }
        number
        frame {
          id
          labeledPalletsCount
          labeledPallets {
            id
          }
        }
        shape {
          id
          faces {
            id
            side
            parentShape {
              id
              faces {
                id
              }
            }
            corners {
              id
              x
              y
              visibility
            }
          }
        }
        load {
          id
          faces {
            id
            side
            parentShape {
              id
              faces {
                id
              }
            }
            corners {
              id
              x
              y
              visibility
            }
          }
        }
      }
    }
  }
`;

let tempID = 0;
function createLabeledPallet(
  viewer: FrameViewer_viewer,
  environment: Environment,
  input: CreateLabeledPalletMutationInput,
  onCompleted?: (
    response: CreateLabeledPalletMutationResponse | null,
    errors: Array<PayloadError> | null
  ) => void,
  onError?: (error: Error) => void
) {
  let mutationId: string = (tempID++).toString();
  const fullInput: CreateLabeledPalletMutationInput = {
    clientMutationId: mutationId,
    ...input
  };
  commitMutation(environment, {
    mutation,
    variables: {
      input: fullInput
    },
    configs: [],
    onCompleted,
    onError,
    optimisticUpdater: store => {
      let frame = store.get(input.frameId);
      if (!frame) {
        return;
      }
      const author = store.get(viewer.id);
      if (!author) {
        return;
      }
      let existingLabels = frame.getLinkedRecords("labeledPallets");
      if (!existingLabels) {
        existingLabels = [];
      }
      const labelId = "client:createLabelLabel:" + mutationId;
      const shapeId = "client:createLabelShape:" + mutationId;
      const faceId = "client:createLabelFace:" + mutationId;
      const loadId = "client:createLabelLoad:" + mutationId;
      const cornerId = "client:createLabelCorner:" + mutationId;

      let label = store.create(labelId, "LabeledPallet");
      let shape = store.create(shapeId, "Cuboid");
      let face = store.create(faceId, "CuboidFace");
      let load = store.create(loadId, "Cuboid");
      let corner = store.create(cornerId, "CuboidCorner");

      label.setValue(labelId, "id");
      label.setValue("", "number");
      label.setValue(input.knownObjectType, "objectType");
      label.setValue(input.surfaceType, "surfaceType");
      shape.setValue(shapeId, "id");
      load.setValue(loadId, "id");
      face.setValue(faceId, "id");
      corner.setValue(cornerId, "id");

      // FIXME(malcolm): Support optimistic updates for pallets with more than one initial corner.
      corner.setValue(input.faces[0].newCorners[0].x, "x");
      corner.setValue(input.faces[0].newCorners[0].y, "y");
      corner.setValue(input.faces[0].newCorners[0].visibility, "visibility");
      corner.setValue(cornerId, "id");

      face.setLinkedRecords([corner], "corners");
      shape.setLinkedRecords([face], "faces");
      shape.setLinkedRecords([corner], "corners");

      face.setLinkedRecord(shape, "parentShape");

      label.setLinkedRecord(shape, "shape");
      label.setLinkedRecord(load, "load");
      label.setLinkedRecord(author, "author");
      label.setLinkedRecord(frame, "frame");

      frame.setLinkedRecords([label, ...existingLabels], "labeledPallets");
    }
  });
}

export default createLabeledPallet;
